import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { AanAfwezigheid } from 'src/app/afas-classes/aanafwezigheid.class';
import { Planning } from 'src/app/classes/planning.class';
import { Realisation, RealisationHourtype } from 'src/app/classes/realisation';
import { Status } from 'src/app/classes/status.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { StatusService } from 'src/app/services/planning/status.service';
import { LocalStorage } from 'src/app/storage.class';
import { Utils } from 'src/app/utils.class';
import { DagrapportStatus } from 'src/environments/trackline/status.dagrapport';

interface WeekSchedulePlanningItem {
    begindate: Date;
    enddate: Date;
    planning?: Planning;
    source?: 'user_planning' | 'planning_has' | 'main';
    rowId?: number;
    comment?: string;
    dayOff?: boolean;
    dayOffReason?: string;
    hourtype?: RealisationHourtype;
    dagrapportStatus?: Status;
}

@Component({
    selector: 'app-week-schedule-planning',
    templateUrl: './week-schedule-planning.component.html',
    standalone: false,
})
export class WeekSchedulePlanningComponent extends CodaltComponent implements OnChanges {
    @Input() beginDate: Date;
    @Input() endDate: Date;
    @Input() maxDate: Date;
    @Input() realisations: Realisation[];
    @Input() aanAfwezigheid: AanAfwezigheid[];

    planningItems: WeekSchedulePlanningItem[] = [];

    constructor(
        private planningService: PlanningService,
        private statusService: StatusService,
        private router: Router
    ) {
        super();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.beginDate || changes.endDate) {
            this.loadData();
        }
    }

    openPlanning(planningId: number, source: WeekSchedulePlanningItem['source'], rowId: number) {
        this.router.navigate(['weekschedule', 'planning', planningId], {
            queryParams: { source, rowId },
        });
    }

    private loadData() {
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();

        const toDate = new Date(this.endDate);
        toDate.setDate(toDate.getDate() + 1);

        this.subscriptions.add(
            combineLatest([
                this.planningService.getFilteredList(this.beginDate, toDate),
                this.statusService.getList('dagrapport'),
            ]).subscribe(([plannings, dagrapportStatusList]) => {
                // No dayreport row yet = same as a freshly opened one: defaults to DagrapportGepland, see day-report-form.component.ts.
                const defaultDagrapportStatus = dagrapportStatusList.find(
                    status => status.key === DagrapportStatus.DagrapportGepland
                );

                const userId = LocalStorage.getUser()?.id;
                this.planningItems = plannings.flatMap(planning => {
                    const dagrapportStatusKey = planning.dagrapporten?.[0]?.status_key;
                    const dagrapportStatus =
                        dagrapportStatusList.find(status => status.key === dagrapportStatusKey) ??
                        defaultDagrapportStatus;
                    const planningItems: WeekSchedulePlanningItem[] = [
                        ...(planning.user_planning ?? [])
                            .filter(userPlanning => userPlanning.user_id === userId)
                            .map(planningItem => ({ planningItem, source: 'user_planning' as const })),
                        ...(planning.planning_has ?? [])
                            .filter(planningHas => planningHas.driver_user_id === userId)
                            .map(planningItem => ({ planningItem, source: 'planning_has' as const })),
                    ].map(({ planningItem, source }) => ({
                        begindate: new Date(planningItem.begindate),
                        enddate: new Date(planningItem.enddate),
                        planning,
                        source,
                        rowId: planningItem.id,
                        comment: planningItem.comment,
                        dagrapportStatus,
                    }));

                    if (planning.performer === userId && planning.mainPlanning && planningItems.length === 0) {
                        planningItems.push({
                            begindate: new Date(planning.mainPlanning.begindate),
                            enddate: new Date(planning.mainPlanning.enddate),
                            planning,
                            source: 'main',
                            rowId: planning.mainPlanning.id,
                            dagrapportStatus,
                        });
                    }

                    return planningItems;
                });
                this.planningItems.push(
                    ...this.realisations
                        .filter(
                            realisation =>
                                !!realisation.leave_id &&
                                Utils.getTimeOrNull(realisation.begindate) < this.maxDate.getTime()
                        )
                        .map(realisation => ({
                            begindate: new Date(realisation.begindate),
                            enddate: new Date(realisation.enddate),
                            comment: realisation.leave?.comment,
                            dayOff: true,
                            hourtype: realisation.hourtype,
                        }))
                );

                for (let i = 0; i <= 6; i++) {
                    const loopDay = Utils.setTime(new Date(this.beginDate), 0, 0);
                    loopDay.setDate(loopDay.getDate() + i);
                    if (
                        !this.planningItems.some(item => Utils.dateString(item.begindate) === Utils.dateString(loopDay))
                    ) {
                        this.planningItems.push({
                            dayOff: true,
                            begindate: loopDay,
                            enddate: Utils.setTimeNewDate(loopDay, 23, 59, 59),
                        });
                    }
                }

                this.aanAfwezigheid.forEach(aanAfwezig => {
                    const planningItem = this.planningItems.find(
                        item =>
                            item.dayOff && Utils.dateString(item.begindate) === Utils.dateString(aanAfwezig.startDate)
                    );
                    if (planningItem) {
                        planningItem.dayOffReason = Utils.minutes(aanAfwezig.hours) + ' - ' + aanAfwezig.omschrijving;
                    }
                });

                this.planningItems.sort(
                    (a, b) =>
                        Utils.getTimeOrNull(a.dayOff ? a.enddate : a.begindate) -
                        Utils.getTimeOrNull(b.dayOff ? b.enddate : b.begindate)
                );
            })
        );
    }
}
